import { useEffect } from "react";
import { X } from "lucide-react";

export default function AdminFormModal({ open, title, onClose, onSubmit, submitting, children, submitLabel = "Save" }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0D0E13] border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
          <h2 className="text-white font-display text-lg font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit?.(e);
          }}
          className="flex-1 overflow-y-auto"
        >
          <div className="px-6 py-5 space-y-4">{children}</div>

          <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-3 sticky bottom-0 bg-[#0D0E13]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {submitting ? "Saving..." : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reusable input primitives
export function Field({ label, children, required, hint }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-gray-600 mt-1">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full bg-[#0A0B10] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#7B2CBF] focus:ring-1 focus:ring-[#7B2CBF] transition-colors";

export function TextInput({ type = "text", ...props }) {
  return <input type={type} {...props} className={`${inputCls} ${props.className || ""}`} />;
}
export function NumberInput(props) {
  return <input type="number" {...props} className={`${inputCls} ${props.className || ""}`} />;
}
export function TextArea(props) {
  return <textarea rows={3} {...props} className={`${inputCls} resize-y ${props.className || ""}`} />;
}
export function Select({ children, ...props }) {
  return (
    <select {...props} className={`${inputCls} ${props.className || ""}`}>
      {children}
    </select>
  );
}
